import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { registerUser, googleLoginUser } from "../services/userAuth.service";
import { signInWithPopup } from "firebase/auth";
import { auth, provider } from "../../firebase";
import api from "../../utils/api";

export default function Register() {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [isReferralLocked, setIsReferralLocked] = useState(false);
  const [loading, setLoading] = useState(false);

  // ================= INITIAL LOAD =================
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      navigate("/user/dashboard", { replace: true });
      return;
    }

    const savedReferralCode = localStorage.getItem("referralCode");
    if (savedReferralCode) {
      setReferralCode(savedReferralCode);
      setIsReferralLocked(true);
    }
  }, [navigate]);

  // ================= APPLY REFERRAL FUNCTION =================
  const applyReferralIfExists = async (newUserId) => {
    if (!referralCode) return;

    try {
      await api.patch(
        "/client/user/validateAndApplyReferralCode",
        null,
        {
          params: {
            userId: newUserId,
            referralCode: referralCode,
          },
        }
      );

      localStorage.removeItem("referralCode");
    } catch (err) {
      console.log("Referral apply failed", err);
    }
  };

  // ================= NORMAL REGISTER =================
  const handleRegister = async () => {
    if (!email || !password || !confirmPassword) {
      return toast.error("Please fill all details");
    }

    if (password !== confirmPassword) {
      return toast.error("Password and Confirm Password do not match");
    }

    try {
      setLoading(true);

      const res = await registerUser(email.trim(), password.trim());

      if (res?.status === true && res?.user) {
        localStorage.setItem("userId", res.user._id);
        localStorage.setItem("user", JSON.stringify(res.user));

        await applyReferralIfExists(res.user._id);

        toast.success(res.message || "Account created successfully");

        if (!res.user.ipAddress || !res.user.country) {
          navigate("/fill-profile", { replace: true });
        } else {
          navigate("/user/dashboard", { replace: true });
        }
      } else {
        toast.error(res?.message || "Signup failed");
      }
    } catch {
      toast.error("Signup failed");
    } finally {
      setLoading(false);
    }
  };

  // ================= GOOGLE REGISTER =================
  const handleGoogleRegister = async () => {
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      const res = await googleLoginUser({
        email: user.email,
        identity: user.uid,
      });

      if (res?.status === true && res?.user) {
        localStorage.setItem("userId", res.user._id);
        localStorage.setItem("user", JSON.stringify(res.user));

        await applyReferralIfExists(res.user._id);

        toast.success("Google account connected successfully");

        if (!res.user.ipAddress || !res.user.country) {
          navigate("/fill-profile", { replace: true });
        } else {
          navigate("/user/dashboard", { replace: true });
        }
      } else {
        toast.error(res?.message || "Google signup failed");
      }
    } catch {
      toast.error("Google signup failed");
    }
  };

  return (
    <div style={page}>
      <div style={card}>
        <h2 style={{ marginBottom: 20, textAlign: "center" }}>
          Create your account
        </h2>

        <input
          style={input}
          type="email"
          placeholder="Email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />

        <input
          style={input}
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />

        <input
          style={input}
          type="password"
          placeholder="Confirm password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
        />

        {/* ================= REFERRAL INPUT ================= */}
        <input
          style={{
            ...input,
            background: isReferralLocked ? "#f5f5f5" : "#fff",
          }}
          type="text"
          placeholder="Referral Code (Optional)"
          value={referralCode}
          onChange={(e) => setReferralCode(e.target.value)}
          disabled={isReferralLocked}
        />

        {/* NORMAL REGISTER BUTTON */}
        <button style={primaryBtn} onClick={handleRegister} disabled={loading}>
          {loading ? "Creating account..." : "Sign Up"}
        </button>

        <div style={divider}>
          <span style={dividerText}>OR</span>
        </div>

        {/* GOOGLE REGISTER BUTTON */}
        <button style={googleBtn} onClick={handleGoogleRegister}>
          <img
            src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
            alt="Google"
            style={{ width: 18, marginRight: 10 }}
          />
          Continue with Google
        </button>

        <p style={{ marginTop: 18, textAlign: "center" }}>
          Already have an account?{" "}
          <span style={link} onClick={() => navigate("/login")}>
            Login
          </span>
        </p>
      </div>
    </div>
  );
}

/* ================= STYLES ================= */

const page = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "#f5f6fa",
};

const card = {
  width: 380,
  padding: 28,
  background: "#fff",
  borderRadius: 12,
  boxShadow: "0 15px 40px rgba(0,0,0,0.1)",
};

const input = {
  width: "100%",
  padding: 12,
  marginBottom: 12,
  borderRadius: 8,
  border: "1px solid #ddd",
  fontSize: 14,
};

const primaryBtn = {
  width: "100%",
  padding: 12,
  borderRadius: 8,
  border: "none",
  background: "#ff4d67",
  color: "#fff",
  fontSize: 15,
  fontWeight: "bold",
  cursor: "pointer",
};

const googleBtn = {
  width: "100%",
  padding: 11,
  borderRadius: 8,
  border: "1px solid #ddd",
  background: "#fff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  fontSize: 14,
};

const divider = {
  display: "flex",
  alignItems: "center",
  margin: "18px 0",
};

const dividerText = {
  margin: "0 auto",
  fontSize: 12,
  color: "#999",
};

const link = {
  color: "#ff4d67",
  cursor: "pointer",
  fontWeight: 500,
};
